/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';
import { Card, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { AccessSessionItem, AccessListResponse } from '../../services/statistics.service';

const { Title, Text } = Typography;

interface AccessSessionTableProps {
  loading?: boolean;
  data?: AccessListResponse;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  showDuration?: boolean;
}

export const AccessSessionTable: React.FC<AccessSessionTableProps> = ({
  loading = false,
  data,
  page,
  pageSize,
  onPageChange,
  showDuration = false,
}) => {
  const totalRows = data?.total ?? 0;

  const columns: ColumnsType<AccessSessionItem> = [
    {
      title: 'Session ID',
      dataIndex: 'sessionId',
      key: 'sessionId',
      render: (v) => v || '',
    },
    {
      title: 'Thời điểm truy cập',
      dataIndex: 'accessTime',
      key: 'accessTime',
      render: (value) => {
        if (!value) return '';
        const date = new Date(value);
        if (!Number.isFinite(date.getTime())) return '';
        return new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Ho_Chi_Minh', year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
        }).format(date).replace(',', '');
      },
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (v) => (v !== null && v !== undefined ? v : ''),
    },
    {
      title: 'Họ và Tên',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (v) => v || '',
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'phone',
      key: 'phone',
      render: (v) => v || '',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v) => v || '',
    },
    {
      title: 'Giới tính',
      dataIndex: 'gender',
      key: 'gender',
      render: (v) => v || '',
    },
    {
      title: 'Tỉnh thành',
      dataIndex: 'province',
      key: 'province',
      render: (v) => v || '',
    },
    {
      title: 'Độ tuổi',
      dataIndex: 'age',
      key: 'age',
      render: (v) => (v !== null && v !== undefined ? v : ''),
    },
    {
      title: 'Chức năng truy cập',
      dataIndex: 'feature',
      key: 'feature',
      render: (v) => v || '',
    },
    {
      title: 'Thiết bị truy cập',
      dataIndex: 'deviceType',
      key: 'deviceType',
      render: (v) => {
        const map: Record<string, string> = { MOBILE: 'Mobile', TABLET: 'Tablet', DESKTOP: 'Desktop' };
        return map[v] || v || '';
      },
    },
    ...(showDuration
      ? [
          {
            title: 'Thời gian (phút)',
            dataIndex: 'duration',
            key: 'duration',
            render: (v: number) => (v !== null && v !== undefined ? (v / 60).toFixed(1) : ''),
          },
        ]
      : []),
  ];

  return (
    <Card css={cardStyle}>
      <div css={headerRowStyle}>
        <Title level={4} style={{ margin: 0 }}>
          Danh sách chi tiết
        </Title>
      </div>
      <div css={headerRowStyle}>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Tổng số: <b>{totalRows.toLocaleString('vi-VN')}</b> kết quả
        </Text>
      </div>

      <Table<AccessSessionItem>
        rowKey={(r, idx) => r.logId ?? `${r.sessionId || idx}_${r.accessTime || idx}_${idx}`}
        columns={columns}
        dataSource={data?.items || []}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize,
          total: totalRows,
          onChange: onPageChange,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          locale: { items_per_page: '/ trang' },
        }}
      />
    </Card>
  );
};

const cardStyle = css`
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const headerRowStyle = css`
  margin-bottom: 16px;
`;
